// FAQ content for Tigik — 5 languages (sk/en/cs/de/pl)
// Each FAQ "answer" is rendered via small render helpers so we avoid
// duplicating JSX 5×. Only the strings differ between languages.

import React from "react";
import { Smartphone, Bell, CreditCard, HelpCircle } from "lucide-react";

const MAIL = "info@saubereko.eu";
const ML = (text) => (
  <a className="text-gold-400 underline" href={`mailto:${MAIL}`}>{text || MAIL}</a>
);

// Reusable answer renderer for ordered list + optional tip
const OL = (steps, tip) => (
  <>
    <ol className="list-decimal pl-5 space-y-1">
      {steps.map((s, i) => <li key={i}>{s}</li>)}
    </ol>
    {tip && <p className="text-zinc-500 mt-2 text-xs">{tip}</p>}
  </>
);

// strong helper
const S = (txt) => <strong>{txt}</strong>;
const G = (txt) => <strong className="text-gold-400">{txt}</strong>;

const T = {
  sk: {
    pageTag: "FAQ",
    pageTitle: "Časté otázky",
    pageSub: (
      <>Inštalácia · notifikácie · platby · inzeráty. Niečo chýba? Napíš na {ML()}.</>
    ),
    catInstall: "Inštalácia",
    catNotif: "Notifikácie",
    catPay: "Platby & faktúry",
    catJobs: "Inzerát práce",
    noAnswer: "Nenašli ste odpoveď?",
    askLabel: "info@saubereko.eu",
  },
  en: {
    pageTag: "FAQ",
    pageTitle: "Frequently Asked Questions",
    pageSub: (
      <>Installation · notifications · payments · job ads. Anything missing? Email {ML()}.</>
    ),
    catInstall: "Installation",
    catNotif: "Notifications",
    catPay: "Payments & invoices",
    catJobs: "Job ads",
    noAnswer: "Didn't find your answer?",
    askLabel: "info@saubereko.eu",
  },
  cs: {
    pageTag: "FAQ",
    pageTitle: "Časté dotazy",
    pageSub: (
      <>Instalace · notifikace · platby · inzeráty. Něco chybí? Napiš na {ML()}.</>
    ),
    catInstall: "Instalace",
    catNotif: "Notifikace",
    catPay: "Platby & faktury",
    catJobs: "Inzerát práce",
    noAnswer: "Nenašli jste odpověď?",
    askLabel: "info@saubereko.eu",
  },
  de: {
    pageTag: "FAQ",
    pageTitle: "Häufige Fragen",
    pageSub: (
      <>Installation · Benachrichtigungen · Zahlungen · Anzeigen. Etwas fehlt? Schreiben Sie an {ML()}.</>
    ),
    catInstall: "Installation",
    catNotif: "Benachrichtigungen",
    catPay: "Zahlungen & Rechnungen",
    catJobs: "Stellenanzeigen",
    noAnswer: "Keine Antwort gefunden?",
    askLabel: "info@saubereko.eu",
  },
  pl: {
    pageTag: "FAQ",
    pageTitle: "Najczęstsze pytania",
    pageSub: (
      <>Instalacja · powiadomienia · płatności · ogłoszenia. Coś brakuje? Napisz na {ML()}.</>
    ),
    catInstall: "Instalacja",
    catNotif: "Powiadomienia",
    catPay: "Płatności i faktury",
    catJobs: "Ogłoszenia o pracę",
    noAnswer: "Nie znalazłeś odpowiedzi?",
    askLabel: "info@saubereko.eu",
  },
  fr: {
    pageTag: "FAQ",
    pageTitle: "Questions fréquentes",
    pageSub: (
      <>Installation · notifications · paiements · annonces. Quelque chose manque ? Écris à {ML()}.</>
    ),
    catInstall: "Installation",
    catNotif: "Notifications",
    catPay: "Paiements & factures",
    catJobs: "Annonce d'emploi",
    noAnswer: "Tu n'as pas trouvé de réponse ?",
    askLabel: "info@saubereko.eu",
  },
};

// ============ Per-language FAQ items ============
const ITEMS = {
  sk: {
    install: [
      {
        q: "Ako si nainštalujem Tigik do mobilu (iPhone)?",
        a: OL([
          <>Otvor {S("https://tigik.saubereko.eu")} v {S("Safari")} (nie Chrome).</>,
          <>Klikni na tlačidlo {S("Zdieľať")} dole v strede (štvorček so šípkou hore).</>,
          <>Posuň dole a klikni {S("„Pridať na plochu\" / „Add to Home Screen\"")}.</>,
          <>Klikni {S("Pridať")} vpravo hore. Tigik sa zobrazí ako natívna appka.</>,
        ], "Tip: ak chceš plnú funkcionalitu PWA (notifikácie, offline), musíš použiť Safari, nie Chrome na iOS."),
      },
      {
        q: "Ako si nainštalujem Tigik do mobilu (Android)?",
        a: OL([
          <>Otvor {S("https://tigik.saubereko.eu")} v Chrome.</>,
          <>Po pár sekundách sa zobrazí lišta {S("„Stiahnuť Tigik\"")} dole — klikni.</>,
          <>Alebo: 3 bodky vpravo hore → {S("„Inštalovať aplikáciu\"")}.</>,
          <>Tigik sa zobrazí ako natívna appka v menu aj na ploche.</>,
        ]),
      },
      {
        q: "Ako si nainštalujem Tigik na PC / notebook?",
        a: OL([
          <>Otvor {S("https://tigik.saubereko.eu")} v Chrome alebo Edge.</>,
          <>V adresnom riadku vpravo uvidíš ikonu {S("monitora so šípkou ⊕")}.</>,
          <>Klikni → {S("„Inštalovať Tigik\"")}.</>,
          <>Appka sa pridá ako samostatné okno v Štart menu (Windows) / Aplikácie (Mac).</>,
        ]),
      },
      {
        q: "Mám starú ikonu / verziu. Ako aktualizovať?",
        a: OL([
          <>Otvor Tigik a klikni {S("„Obnoviť\"")} v žltej lište hore (ak svieti).</>,
          <>Ak nesvieti: pridrž ikonu Tigik na mobile → odstrániť → znova pridať na plochu.</>,
          <>PC: Ctrl+Shift+R (force refresh) v Tigik appke.</>,
        ]),
      },
    ],
    notif: [
      {
        q: "Prečo mi neprichádzajú zvukové oznámenia v chate?",
        a: OL([
          <>Otvor {S("Komunita")} v Tigik.</>,
          <>Hore v hlavičke chatu klikni {S("„Povoliť oznámenia\"")}.</>,
          <>Mobilný OS sa spýta — povol notifikácie pre Tigik.</>,
          <>Pri @označení dostaneš zvuk + push notifikáciu.</>,
        ], "iOS Safari nepodporuje push notifikácie pre web pages, len pre nainštalované PWA. Najprv si Tigik nainštaluj na plochu (pozri Inštalácia)."),
      },
    ],
    pay: [
      {
        q: "Aké plány Tigik ponúka?",
        a: (
          <ul className="list-disc pl-5 space-y-1">
            <li>{G("START — €1.99 / 2 dni")} · Manual TIG (DN ≤ 85, stena ≤ 2 mm), komunita (čítanie + chat), 2 AI/deň</li>
            <li>{G("ACTIVE — €3.99 / 7 dní")} · orbital + TIG bez limitu + potrubár + komunita všetky kanály + 3 AI/deň</li>
            <li>{G("PRO — €6.99 / 30 dní")} · všetko z ACTIVE + AI poradca 5 správ/deň + prioritná podpora</li>
          </ul>
        ),
      },
      {
        q: "Ako dostanem faktúru?",
        a: (
          <>Faktúra ti príde e-mailom hneď po platbe na adresu, na ktorú si zaregistrovaný.
          Ceny sú konečné a uvedené vrátane 23 % DPH (SauberEko s. r. o. je platca DPH). Pre B2B odberateľov v EÚ s platným IČ DPH sa uplatňuje prenesenie daňovej povinnosti (reverse-charge, čl. 44 smernice 2006/112/ES). Pre B2C cezhraničné digitálne služby v EÚ platí OSS schéma (One-Stop-Shop) so sadzbou DPH štátu spotreby.<br/>
          Potrebuješ PDF? Napíš na {ML()}.</>
        ),
      },
      {
        q: "Dá sa platba refundovať?",
        a: <>Ak nemáš s Tigikom skúsenosti a uplynulo menej než 14 dní od prvej platby — napíš na {ML()} a vrátime ti peniaze v zmysle z. č. 102/2014 Z. z.</>,
      },
    ],
    jobs: [
      {
        q: "Koľko stojí inzerát?",
        a: <>{G("Ponuka práce = €3 za 14 dní")}. Kategórie {S("Zváracia technika")} a {S("Ďalšie")} sú {G("zdarma")} — prejdú len admin kontrolou (proti spamu). Po 14 dňoch inzerát vyprší, môžeš ho zverejniť znova.</>,
      },
      {
        q: "Ako pridám inzerát?",
        a: OL([
          <>Otvor {S("Inzercia")} v menu.</>,
          <>Klikni {S("„Pridať inzerát\"")}.</>,
          <>Vyber kategóriu — {S("Ponuka práce")} (€3), {S("Zváracia technika")} alebo {S("Ďalšie")} (zdarma).</>,
          <>Vyplň formulár (titul, firma/predajca, popis, kontakt) a klikni {S("„Odoslať na schválenie\"")}.</>,
          <>Pre {S("Ponuku práce")} ťa Stripe presmeruje na platbu €3 — po zaplatení pôjde na admin kontrolu.</>,
          <>Po schválení sa inzerát okamžite zverejní (14 dní live) a dostaneš potvrdzujúci email.</>,
        ]),
      },
    ],
  },
  en: {
    install: [
      {
        q: "How do I install Tigik on my phone (iPhone)?",
        a: OL([
          <>Open {S("https://tigik.saubereko.eu")} in {S("Safari")} (not Chrome).</>,
          <>Tap the {S("Share")} button at the bottom centre (square with up arrow).</>,
          <>Scroll down and tap {S("„Add to Home Screen\"")}.</>,
          <>Tap {S("Add")} in the top-right. Tigik will appear as a native app.</>,
        ], "Tip: for full PWA functionality (notifications, offline) you must use Safari, not Chrome on iOS."),
      },
      {
        q: "How do I install Tigik on my phone (Android)?",
        a: OL([
          <>Open {S("https://tigik.saubereko.eu")} in Chrome.</>,
          <>After a few seconds a {S("„Install Tigik\"")} bar appears at the bottom — tap it.</>,
          <>Or: 3-dot menu top right → {S("„Install app\"")}.</>,
          <>Tigik appears as a native app on the home screen and in the launcher.</>,
        ]),
      },
      {
        q: "How do I install Tigik on a PC / laptop?",
        a: OL([
          <>Open {S("https://tigik.saubereko.eu")} in Chrome or Edge.</>,
          <>On the right side of the address bar you'll see an icon — {S("monitor with arrow ⊕")}.</>,
          <>Click → {S("„Install Tigik\"")}.</>,
          <>The app is added as a standalone window in the Start menu (Windows) / Applications (Mac).</>,
        ]),
      },
      {
        q: "I have an old icon / version. How to update?",
        a: OL([
          <>Open Tigik and click {S("„Refresh\"")} in the yellow bar at the top (if visible).</>,
          <>If not visible: long-press the Tigik icon on mobile → remove → add to home screen again.</>,
          <>PC: Ctrl+Shift+R (force refresh) inside the Tigik app.</>,
        ]),
      },
    ],
    notif: [
      {
        q: "Why don't I get sound notifications in chat?",
        a: OL([
          <>Open {S("Community")} in Tigik.</>,
          <>In the chat header tap {S("„Enable notifications\"")}.</>,
          <>The mobile OS will ask — allow notifications for Tigik.</>,
          <>On an @mention you'll get a sound + push notification.</>,
        ], "iOS Safari does not support push notifications for web pages, only for installed PWAs. Install Tigik on the home screen first (see Installation)."),
      },
    ],
    pay: [
      {
        q: "What plans does Tigik offer?",
        a: (
          <ul className="list-disc pl-5 space-y-1">
            <li>{G("START — €1.99 / 2 days")} · Manual TIG (DN ≤ 85, wall ≤ 2 mm), community (read + chat), 2 AI/day</li>
            <li>{G("ACTIVE — €3.99 / 7 days")} · orbital + TIG unlimited + pipefitter + community all channels + 3 AI/day</li>
            <li>{G("PRO — €6.99 / 30 days")} · everything in ACTIVE + AI advisor 5 messages/day + priority support</li>
          </ul>
        ),
      },
      {
        q: "How do I get an invoice?",
        a: (
          <>You'll receive the invoice by email immediately after payment, at the address you registered with.
          Prices are final and shown including 23 % VAT (SauberEko s. r. o. is a registered Slovak VAT payer). For EU B2B customers with a valid VAT ID, the reverse-charge mechanism applies (Art. 44 of Directive 2006/112/EC). For cross-border EU B2C digital services, the OSS (One-Stop-Shop) scheme is applied at the destination-country VAT rate.<br/>
          Need a PDF? Email {ML()}.</>
        ),
      },
      {
        q: "Can the payment be refunded?",
        a: <>If you have no experience with Tigik and less than 14 days have passed since your first payment — email {ML()} and we'll refund you in line with Slovak Act 102/2014.</>,
      },
    ],
    jobs: [
      {
        q: "How much does an ad cost?",
        a: <>{G("Job offers = €3 for 14 days")}. {S("Welding equipment")} and {S("Other")} categories are {G("free")} — they only go through an admin review (anti-spam). After 14 days the ad expires and you can repost it.</>,
      },
      {
        q: "How do I post an ad?",
        a: OL([
          <>Open {S("Classifieds")} in the menu.</>,
          <>Click {S("„Post ad\"")}.</>,
          <>Pick a category — {S("Job offer")} (€3), {S("Welding equipment")} or {S("Other")} (free).</>,
          <>Fill in the form (title, company/seller, description, contact) and click {S("„Submit for review\"")}.</>,
          <>For a {S("Job offer")} Stripe will redirect you to a €3 payment — after paying it goes to admin review.</>,
          <>Once approved, the ad goes live instantly (14 days) and you receive a confirmation email.</>,
        ]),
      },
    ],
  },
  cs: {
    install: [
      {
        q: "Jak si nainstaluji Tigik do mobilu (iPhone)?",
        a: OL([
          <>Otevři {S("https://tigik.saubereko.eu")} v {S("Safari")} (ne Chrome).</>,
          <>Klikni na tlačítko {S("Sdílet")} dole uprostřed (čtvereček se šipkou nahoru).</>,
          <>Sjeď dolů a klikni {S("„Přidat na plochu\" / „Add to Home Screen\"")}.</>,
          <>Klikni {S("Přidat")} vpravo nahoře. Tigik se zobrazí jako nativní aplikace.</>,
        ], "Tip: pokud chceš plnou funkcionalitu PWA (notifikace, offline), musíš použít Safari, ne Chrome na iOS."),
      },
      {
        q: "Jak si nainstaluji Tigik do mobilu (Android)?",
        a: OL([
          <>Otevři {S("https://tigik.saubereko.eu")} v Chrome.</>,
          <>Po pár vteřinách se dole zobrazí lišta {S("„Stáhnout Tigik\"")} — klikni.</>,
          <>Nebo: 3 tečky vpravo nahoře → {S("„Nainstalovat aplikaci\"")}.</>,
          <>Tigik se zobrazí jako nativní aplikace v menu i na ploše.</>,
        ]),
      },
      {
        q: "Jak si nainstaluji Tigik na PC / notebook?",
        a: OL([
          <>Otevři {S("https://tigik.saubereko.eu")} v Chrome nebo Edge.</>,
          <>V adresním řádku vpravo uvidíš ikonu {S("monitoru se šipkou ⊕")}.</>,
          <>Klikni → {S("„Nainstalovat Tigik\"")}.</>,
          <>Aplikace se přidá jako samostatné okno do Start menu (Windows) / Aplikace (Mac).</>,
        ]),
      },
      {
        q: "Mám starou ikonu / verzi. Jak aktualizovat?",
        a: OL([
          <>Otevři Tigik a klikni {S("„Obnovit\"")} ve žluté liště nahoře (pokud svítí).</>,
          <>Pokud nesvítí: podrž ikonu Tigik na mobilu → odstranit → znovu přidat na plochu.</>,
          <>PC: Ctrl+Shift+R (force refresh) v Tigik aplikaci.</>,
        ]),
      },
    ],
    notif: [
      {
        q: "Proč mi nechodí zvuková oznámení v chatu?",
        a: OL([
          <>Otevři {S("Komunita")} v Tigik.</>,
          <>Nahoře v hlavičce chatu klikni {S("„Povolit oznámení\"")}.</>,
          <>Mobilní OS se zeptá — povol notifikace pro Tigik.</>,
          <>Při @označení dostaneš zvuk + push notifikaci.</>,
        ], "iOS Safari nepodporuje push notifikace pro webové stránky, pouze pro nainstalované PWA. Nejdřív si Tigik nainstaluj na plochu (viz Instalace)."),
      },
    ],
    pay: [
      {
        q: "Jaké plány Tigik nabízí?",
        a: (
          <ul className="list-disc pl-5 space-y-1">
            <li>{G("START — €1,99 / 2 dny")} · Manual TIG (DN ≤ 85, stěna ≤ 2 mm), komunita (čtení + chat), 2 AI/den</li>
            <li>{G("ACTIVE — €3,99 / 7 dní")} · orbital + TIG bez limitu + pipefitter + komunita všechny kanály + 3 AI/den</li>
            <li>{G("PRO — €6,99 / 30 dní")} · vše z ACTIVE + AI poradce 5 zpráv/den + prioritní podpora</li>
          </ul>
        ),
      },
      {
        q: "Jak dostanu fakturu?",
        a: (
          <>Faktura ti přijde e-mailem ihned po platbě na adresu, na kterou jsi registrovaný.
          Ceny jsou konečné a uvedeny včetně 23 % DPH (SauberEko s. r. o. je plátce DPH). Pro B2B odběratele v EU s platným DIČ se uplatňuje přenesení daňové povinnosti (reverse-charge). Pro B2C přeshraniční digitální služby v EU platí schéma OSS (One-Stop-Shop).<br/>
          Potřebuješ PDF? Napiš na {ML()}.</>
        ),
      },
      {
        q: "Lze platbu refundovat?",
        a: <>Pokud nemáš s Tigikem zkušenosti a uplynulo méně než 14 dní od první platby — napiš na {ML()} a vrátíme ti peníze podle z. č. 102/2014 Z. z.</>,
      },
    ],
    jobs: [
      {
        q: "Kolik stojí inzerát?",
        a: <>{G("Nabídka práce = €3 na 14 dní")}. Kategorie {S("Svářecí technika")} a {S("Další")} jsou {G("zdarma")} — projdou jen admin kontrolou (proti spamu). Po 14 dnech inzerát vyprší, můžeš ho zveřejnit znovu.</>,
      },
      {
        q: "Jak přidám inzerát?",
        a: OL([
          <>Otevři {S("Inzerce")} v menu.</>,
          <>Klikni {S("„Přidat inzerát\"")}.</>,
          <>Vyber kategorii — {S("Nabídka práce")} (€3), {S("Svářecí technika")} nebo {S("Další")} (zdarma).</>,
          <>Vyplň formulář (titul, firma/prodejce, popis, kontakt) a klikni {S("„Odeslat ke schválení\"")}.</>,
          <>U {S("Nabídky práce")} tě Stripe přesměruje na platbu €3 — po zaplacení jde na admin kontrolu.</>,
          <>Po schválení se inzerát okamžitě zveřejní (14 dní live) a dostaneš potvrzovací email.</>,
        ]),
      },
    ],
  },
  de: {
    install: [
      {
        q: "Wie installiere ich Tigik auf dem iPhone?",
        a: OL([
          <>Öffne {S("https://tigik.saubereko.eu")} in {S("Safari")} (nicht Chrome).</>,
          <>Tippe auf den {S("Teilen")}-Button unten in der Mitte (Quadrat mit Pfeil nach oben).</>,
          <>Scrolle nach unten und tippe auf {S("„Zum Home-Bildschirm hinzufügen\"")}.</>,
          <>Tippe oben rechts auf {S("Hinzufügen")}. Tigik erscheint als native App.</>,
        ], "Tipp: Für die volle PWA-Funktionalität (Benachrichtigungen, Offline) musst du Safari verwenden, nicht Chrome auf iOS."),
      },
      {
        q: "Wie installiere ich Tigik auf einem Android-Handy?",
        a: OL([
          <>Öffne {S("https://tigik.saubereko.eu")} in Chrome.</>,
          <>Nach wenigen Sekunden erscheint unten ein {S("„Tigik herunterladen\"")}-Banner — tippe darauf.</>,
          <>Oder: 3-Punkte-Menü oben rechts → {S("„App installieren\"")}.</>,
          <>Tigik wird als native App im Menü und auf dem Home-Bildschirm angezeigt.</>,
        ]),
      },
      {
        q: "Wie installiere ich Tigik auf PC / Laptop?",
        a: OL([
          <>Öffne {S("https://tigik.saubereko.eu")} in Chrome oder Edge.</>,
          <>Rechts in der Adressleiste siehst du ein {S("Monitor-Symbol mit Pfeil ⊕")}.</>,
          <>Klicke → {S("„Tigik installieren\"")}.</>,
          <>Die App wird als eigenständiges Fenster im Startmenü (Windows) / Programme (Mac) hinzugefügt.</>,
        ]),
      },
      {
        q: "Ich habe ein altes Icon / Version. Wie aktualisieren?",
        a: OL([
          <>Öffne Tigik und klicke auf {S("„Aktualisieren\"")} im gelben Banner oben (falls sichtbar).</>,
          <>Falls nicht sichtbar: Tigik-Icon auf dem Handy gedrückt halten → entfernen → erneut zum Home-Bildschirm hinzufügen.</>,
          <>PC: Strg+Shift+R (Force-Refresh) in der Tigik-App.</>,
        ]),
      },
    ],
    notif: [
      {
        q: "Warum erhalte ich keine Ton-Benachrichtigungen im Chat?",
        a: OL([
          <>Öffne {S("Community")} in Tigik.</>,
          <>Tippe oben in der Chat-Kopfzeile auf {S("„Benachrichtigungen erlauben\"")}.</>,
          <>Das mobile OS fragt — erlaube Benachrichtigungen für Tigik.</>,
          <>Bei einer @-Erwähnung bekommst du einen Ton + Push-Benachrichtigung.</>,
        ], "iOS-Safari unterstützt keine Push-Benachrichtigungen für Webseiten, nur für installierte PWAs. Installiere Tigik zuerst auf dem Home-Bildschirm (siehe Installation)."),
      },
    ],
    pay: [
      {
        q: "Welche Pläne bietet Tigik?",
        a: (
          <ul className="list-disc pl-5 space-y-1">
            <li>{G("START — €1,99 / 2 Tage")} · Manual TIG (DN ≤ 85, Wand ≤ 2 mm), Community (Lesen + Chat), 2 KI/Tag</li>
            <li>{G("ACTIVE — €3,99 / 7 Tage")} · Orbital + TIG ohne Limit + Pipefitter + Community alle Kanäle + 3 KI/Tag</li>
            <li>{G("PRO — €6,99 / 30 Tage")} · alles aus ACTIVE + KI-Berater 5 Nachrichten/Tag + Priority-Support</li>
          </ul>
        ),
      },
      {
        q: "Wie erhalte ich eine Rechnung?",
        a: (
          <>Die Rechnung wird sofort nach der Zahlung per E-Mail an die registrierte Adresse versandt.
          Alle Preise sind inkl. 23 % MwSt. (SauberEko s. r. o. ist umsatzsteuerpflichtig). Für EU-B2B-Kunden mit gültiger USt-IdNr. gilt das Reverse-Charge-Verfahren. Für grenzüberschreitende EU-B2C-Digitalleistungen wird das OSS-Verfahren (One-Stop-Shop) angewendet.<br/>
          PDF benötigt? Schreiben Sie an {ML()}.</>
        ),
      },
      {
        q: "Kann die Zahlung erstattet werden?",
        a: <>Wenn du keine Erfahrung mit Tigik hast und weniger als 14 Tage seit der ersten Zahlung vergangen sind — schreibe an {ML()} und wir erstatten gemäß slowakischem Gesetz 102/2014.</>,
      },
    ],
    jobs: [
      {
        q: "Wie viel kostet eine Anzeige?",
        a: <>{G("Stellenangebote = €3 für 14 Tage")}. Die Kategorien {S("Schweißtechnik")} und {S("Sonstiges")} sind {G("kostenlos")} — sie durchlaufen nur eine Admin-Prüfung (gegen Spam). Nach 14 Tagen läuft die Anzeige ab und kann neu veröffentlicht werden.</>,
      },
      {
        q: "Wie gebe ich eine Anzeige auf?",
        a: OL([
          <>Öffne {S("Kleinanzeigen")} im Menü.</>,
          <>Klicke auf {S("„Anzeige aufgeben\"")}.</>,
          <>Wähle eine Kategorie — {S("Stellenangebot")} (€3), {S("Schweißtechnik")} oder {S("Sonstiges")} (kostenlos).</>,
          <>Fülle das Formular aus (Titel, Firma/Verkäufer, Beschreibung, Kontakt) und klicke {S("„Zur Prüfung senden\"")}.</>,
          <>Bei einem {S("Stellenangebot")} leitet Stripe dich auf die Zahlung von €3 weiter — nach der Zahlung geht die Anzeige zur Admin-Prüfung.</>,
          <>Nach Freigabe wird die Anzeige sofort veröffentlicht (14 Tage live) und du erhältst eine Bestätigungs-E-Mail.</>,
        ]),
      },
    ],
  },
  pl: {
    install: [
      {
        q: "Jak zainstaluję Tigik na telefonie (iPhone)?",
        a: OL([
          <>Otwórz {S("https://tigik.saubereko.eu")} w {S("Safari")} (nie Chrome).</>,
          <>Kliknij przycisk {S("Udostępnij")} na dole pośrodku (kwadrat ze strzałką w górę).</>,
          <>Przewiń w dół i kliknij {S("„Dodaj do ekranu początkowego\"")}.</>,
          <>Kliknij {S("Dodaj")} w prawym górnym rogu. Tigik pojawi się jako natywna aplikacja.</>,
        ], "Wskazówka: aby uzyskać pełną funkcjonalność PWA (powiadomienia, offline), musisz użyć Safari, a nie Chrome na iOS."),
      },
      {
        q: "Jak zainstaluję Tigik na telefonie (Android)?",
        a: OL([
          <>Otwórz {S("https://tigik.saubereko.eu")} w Chrome.</>,
          <>Po kilku sekundach na dole pojawi się pasek {S("„Pobierz Tigik\"")} — kliknij.</>,
          <>Lub: 3 kropki w prawym górnym rogu → {S("„Zainstaluj aplikację\"")}.</>,
          <>Tigik pojawi się jako natywna aplikacja w menu i na ekranie głównym.</>,
        ]),
      },
      {
        q: "Jak zainstaluję Tigik na PC / laptopie?",
        a: OL([
          <>Otwórz {S("https://tigik.saubereko.eu")} w Chrome lub Edge.</>,
          <>W pasku adresu po prawej zobaczysz ikonę {S("monitora ze strzałką ⊕")}.</>,
          <>Kliknij → {S("„Zainstaluj Tigik\"")}.</>,
          <>Aplikacja zostanie dodana jako osobne okno w menu Start (Windows) / Aplikacje (Mac).</>,
        ]),
      },
      {
        q: "Mam starą ikonę / wersję. Jak zaktualizować?",
        a: OL([
          <>Otwórz Tigik i kliknij {S("„Odśwież\"")} na żółtym pasku u góry (jeśli widoczny).</>,
          <>Jeśli niewidoczny: przytrzymaj ikonę Tigik na telefonie → usuń → ponownie dodaj do ekranu głównego.</>,
          <>PC: Ctrl+Shift+R (wymuszony refresh) w aplikacji Tigik.</>,
        ]),
      },
    ],
    notif: [
      {
        q: "Dlaczego nie otrzymuję powiadomień dźwiękowych w czacie?",
        a: OL([
          <>Otwórz {S("Społeczność")} w Tigik.</>,
          <>U góry nagłówka czatu kliknij {S("„Zezwól na powiadomienia\"")}.</>,
          <>System mobilny zapyta — zezwól na powiadomienia dla Tigik.</>,
          <>Przy @oznaczeniu dostaniesz dźwięk + powiadomienie push.</>,
        ], "iOS Safari nie obsługuje powiadomień push dla stron internetowych, tylko dla zainstalowanych PWA. Najpierw zainstaluj Tigik na ekranie głównym (patrz Instalacja)."),
      },
    ],
    pay: [
      {
        q: "Jakie plany oferuje Tigik?",
        a: (
          <ul className="list-disc pl-5 space-y-1">
            <li>{G("START — €1,99 / 2 dni")} · Manual TIG (DN ≤ 85, ścianka ≤ 2 mm), społeczność (czytanie + czat), 2 AI/dzień</li>
            <li>{G("ACTIVE — €3,99 / 7 dni")} · orbital + TIG bez limitu + pipefitter + społeczność wszystkie kanały + 3 AI/dzień</li>
            <li>{G("PRO — €6,99 / 30 dni")} · wszystko z ACTIVE + doradca AI 5 wiadomości/dzień + priorytetowe wsparcie</li>
          </ul>
        ),
      },
      {
        q: "Jak otrzymam fakturę?",
        a: (
          <>Faktura przyjdzie ci e-mailem natychmiast po płatności na adres, na który jesteś zarejestrowany.
          Ceny są ostateczne i podane z 23 % VAT (SauberEko s. r. o. jest płatnikiem VAT). Dla klientów B2B w UE z ważnym numerem VAT UE stosuje się mechanizm odwrotnego obciążenia (reverse-charge). Dla transgranicznych usług cyfrowych B2C w UE stosuje się schemat OSS (One-Stop-Shop).<br/>
          Potrzebujesz PDF? Napisz na {ML()}.</>
        ),
      },
      {
        q: "Czy można uzyskać zwrot płatności?",
        a: <>Jeśli nie masz doświadczenia z Tigik i upłynęło mniej niż 14 dni od pierwszej płatności — napisz na {ML()} a zwrócimy ci pieniądze zgodnie z słowacką ustawą 102/2014 Z. z.</>,
      },
    ],
    jobs: [
      {
        q: "Ile kosztuje ogłoszenie?",
        a: <>{G("Oferty pracy = €3 za 14 dni")}. Kategorie {S("Technika spawalnicza")} i {S("Inne")} są {G("za darmo")} — przechodzą tylko kontrolę admina (antyspam). Po 14 dniach ogłoszenie wygasa, można je opublikować ponownie.</>,
      },
      {
        q: "Jak dodać ogłoszenie?",
        a: OL([
          <>Otwórz {S("Ogłoszenia")} w menu.</>,
          <>Kliknij {S("„Dodaj ogłoszenie\"")}.</>,
          <>Wybierz kategorię — {S("Oferta pracy")} (€3), {S("Technika spawalnicza")} lub {S("Inne")} (za darmo).</>,
          <>Wypełnij formularz (tytuł, firma/sprzedawca, opis, kontakt) i kliknij {S("„Wyślij do akceptacji\"")}.</>,
          <>Dla {S("Oferty pracy")} Stripe przekieruje cię na płatność €3 — po zapłacie ogłoszenie trafi do kontroli admina.</>,
          <>Po akceptacji ogłoszenie pojawi się natychmiast (14 dni live) i otrzymasz email potwierdzający.</>,
        ]),
      },
    ],
  },
  fr: {
    install: [
      {
        q: "Comment installer Tigik sur mon mobile (iPhone) ?",
        a: OL([
          <>Ouvre {S("https://tigik.saubereko.eu")} dans {S("Safari")} (pas Chrome).</>,
          <>Touche le bouton {S("Partager")} en bas au centre (carré avec flèche vers le haut).</>,
          <>Fais défiler et touche {S("« Sur l'écran d'accueil » / « Add to Home Screen »")}.</>,
          <>Touche {S("Ajouter")} en haut à droite. Tigik s'affichera comme une app native.</>,
        ], "Astuce : pour les fonctions PWA complètes (notifications, hors ligne), tu dois utiliser Safari, pas Chrome sur iOS."),
      },
      {
        q: "Comment installer Tigik sur mon mobile (Android) ?",
        a: OL([
          <>Ouvre {S("https://tigik.saubereko.eu")} dans Chrome.</>,
          <>Après quelques secondes, une bannière {S("« Télécharger Tigik »")} apparaîtra en bas — touche-la.</>,
          <>Ou : 3 points en haut à droite → {S("« Installer l'application »")}.</>,
          <>Tigik s'affichera comme une app native dans le menu et sur l'écran d'accueil.</>,
        ]),
      },
      {
        q: "Comment installer Tigik sur PC / portable ?",
        a: OL([
          <>Ouvre {S("https://tigik.saubereko.eu")} dans Chrome ou Edge.</>,
          <>Dans la barre d'adresse à droite, tu verras une icône {S("écran avec flèche ⊕")}.</>,
          <>Clique → {S("« Installer Tigik »")}.</>,
          <>L'app s'ajoutera comme fenêtre autonome dans le menu Démarrer (Windows) / Applications (Mac).</>,
        ]),
      },
      {
        q: "J'ai une vieille icône / version. Comment mettre à jour ?",
        a: OL([
          <>Ouvre Tigik et clique {S("« Actualiser »")} dans la barre jaune en haut (si elle s'affiche).</>,
          <>Si elle ne s'affiche pas : maintiens l'icône Tigik sur mobile → supprimer → rajouter sur l'écran d'accueil.</>,
          <>PC : Ctrl+Shift+R (rechargement forcé) dans l'app Tigik.</>,
        ]),
      },
    ],
    notif: [
      {
        q: "Pourquoi je ne reçois pas de notifications sonores dans le chat ?",
        a: OL([
          <>Ouvre {S("Communauté")} dans Tigik.</>,
          <>En haut dans l'en-tête du chat, clique {S("« Autoriser les notifications »")}.</>,
          <>L'OS mobile te demandera — autorise les notifications pour Tigik.</>,
          <>Quand tu es @mentionné, tu recevras un son + notification push.</>,
        ], "iOS Safari ne prend pas en charge les notifications push pour les pages web, seulement pour les PWA installées. Installe d'abord Tigik sur l'écran d'accueil (voir Installation)."),
      },
    ],
    pay: [
      {
        q: "Quelles formules propose Tigik ?",
        a: (
          <ul className="list-disc pl-5 space-y-1">
            <li>{G("START — 1,99 € / 2 jours")} · TIG manuel (DN ≤ 85, épaisseur ≤ 2 mm), communauté (lecture + chat), 2 IA/jour</li>
            <li>{G("ACTIVE — 3,99 € / 7 jours")} · orbital + TIG sans limite + tuyauteur + tous les canaux communauté + 3 IA/jour</li>
            <li>{G("PRO — 6,99 € / 30 jours")} · tout ACTIVE + conseiller IA 5 messages/jour + support prioritaire</li>
          </ul>
        ),
      },
      {
        q: "Comment obtenir une facture ?",
        a: (
          <>La facture t'arrivera par e-mail juste après le paiement à l'adresse avec laquelle tu es enregistré.
          Les prix sont définitifs et indiqués TTC 23 % (SauberEko s. r. o. est assujetti à la TVA). Pour les clients B2B dans l'UE avec un n° de TVA valide, l'autoliquidation s'applique (reverse-charge, art. 44 directive 2006/112/CE). Pour les services numériques B2C transfrontaliers dans l'UE, le régime OSS (One-Stop-Shop) s'applique avec le taux de TVA du pays de consommation.<br/>
          Besoin d'un PDF ? Écris à {ML()}.</>
        ),
      },
      {
        q: "Le paiement peut-il être remboursé ?",
        a: <>Si tu n'as pas d'expérience avec Tigik et que moins de 14 jours se sont écoulés depuis ton premier paiement — écris à {ML()} et on te remboursera conformément à la loi n° 102/2014 Rec.</>,
      },
    ],
    jobs: [
      {
        q: "Combien coûte une annonce ?",
        a: <>{G("Offre d'emploi = 3 € pour 14 jours")}. Les catégories {S("Matériel de soudage")} et {S("Divers")} sont {G("gratuites")} — elles passent juste un contrôle admin (anti-spam). Après 14 jours, l'annonce expire, tu peux la republier.</>,
      },
      {
        q: "Comment ajouter une annonce ?",
        a: OL([
          <>Ouvre {S("Annonces")} dans le menu.</>,
          <>Clique {S("« Ajouter une annonce »")}.</>,
          <>Choisis la catégorie — {S("Offre d'emploi")} (3 €), {S("Matériel de soudage")} ou {S("Divers")} (gratuit).</>,
          <>Remplis le formulaire (titre, entreprise/vendeur, description, contact) et clique {S("« Envoyer pour approbation »")}.</>,
          <>Pour {S("Offre d'emploi")}, Stripe te redirigera vers le paiement de 3 € — après paiement, passage au contrôle admin.</>,
          <>Après approbation, l'annonce est publiée immédiatement (14 jours en ligne) et tu reçois un e-mail de confirmation.</>,
        ]),
      },
    ],
  },
};

export function getFAQ(lang = "sk") {
  const tr = T[lang] || T.sk;
  const items = ITEMS[lang] || ITEMS.sk;
  return {
    tr,
    sections: [
      { cat: tr.catInstall, icon: Smartphone, items: items.install },
      { cat: tr.catNotif, icon: Bell, items: items.notif },
      { cat: tr.catPay, icon: CreditCard, items: items.pay },
      { cat: tr.catJobs, icon: HelpCircle, items: items.jobs },
    ],
  };
}
